import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import * as React from 'react'
import { usePositionsTable } from '../../../hooks/use-positions-table'
import type { TDiscipline, TPosition } from '../../../types/entities'

/**
 * Computes the optimistic seniority_level values for all positions in the
 * discipline after moving the position with `sourceId` to `targetLevel`.
 *
 * Adjacent forward move to an occupied block (targetLevel === sourceLevel + 1):
 *   swap the two positions — occupant moves back to sourceLevel.
 *
 * All other moves: find the consecutive run of occupied blocks starting at
 *   targetLevel and shift that run right (+1). Blocks separated from the
 *   target by a gap are never affected.
 *
 * Examples:
 *   [2,3,4,5], 3→4 : adjacent+occupied → swap       → [2,3,4,5]
 *   [3,4,5],   5→3 : run=[3,4] shifts  → [3,4,5]
 *   [4,5],     5→1 : target=1 empty, gap before 4   → [1,4]
 *   [2,3,4],   2→4 : run=[4]; 3 unaffected           → [3,4,5]
 */
const applyBlockMove = (
  positions: TPosition[],
  sourceId: number,
  targetLevel: number
): TPosition[] => {
  const others = positions.filter((p) => p.id !== sourceId)
  const source = positions.find((p) => p.id === sourceId)
  if (!source) return positions

  const sourceLevel = source.seniorityLevel ?? 0

  // Adjacent forward move to an occupied block: swap instead of cascading.
  const adjacentOccupant =
    targetLevel === sourceLevel + 1
      ? others.find((p) => (p.seniorityLevel ?? 0) === targetLevel)
      : undefined

  if (adjacentOccupant) {
    const updatedOthers = others.map((p) =>
      p.id === adjacentOccupant.id ? { ...p, seniorityLevel: sourceLevel } : p
    )
    return [...updatedOthers, { ...source, seniorityLevel: targetLevel }].sort(
      (a, b) => (a.seniorityLevel ?? 0) - (b.seniorityLevel ?? 0)
    )
  }

  // General case: find the consecutive run starting at targetLevel and shift it right (+1).
  const occupiedFromTarget = others
    .filter((p) => (p.seniorityLevel ?? 0) >= targetLevel)
    .map((p) => p.seniorityLevel ?? 0)
    .sort((a, b) => a - b)

  let shiftEnd = targetLevel - 1
  for (const lvl of occupiedFromTarget) {
    if (lvl > shiftEnd + 1) break
    shiftEnd = lvl
  }

  const updatedOthers = others.map((p) => {
    const level = p.seniorityLevel ?? 0
    return level >= targetLevel && level <= shiftEnd
      ? { ...p, seniorityLevel: level + 1 }
      : p
  })

  return [...updatedOthers, { ...source, seniorityLevel: targetLevel }].sort(
    (a, b) => (a.seniorityLevel ?? 0) - (b.seniorityLevel ?? 0)
  )
}

const resolveTargetSeniorityLevel = (
  overId: string,
  positions: TPosition[]
): number | null => {
  if (overId.startsWith('pos-')) {
    const posId = parseInt(overId.replace('pos-', ''), 10)
    return positions.find((p) => p.id === posId)?.seniorityLevel ?? null
  }
  if (overId.startsWith('block-')) {
    // ID format: block-{disciplineId}-{seniorityLevel}
    const level = parseInt(overId.split('-').pop() || '', 10)
    return isNaN(level) ? null : level
  }
  return null
}

export const useDraggablePositions = (discipline: TDiscipline) => {
  const [eventHandlers, getPositionColumns, setDisciplines] = usePositionsTable(
    (s) => [s.eventHandlers, s.getPositionColumns, s.setDisciplines]
  )

  // Narrow subscription: only re-renders (and triggers the sync effect below)
  // when THIS discipline's object changes in the store. setDisciplines keeps
  // unmodified discipline objects at the same reference, so Zustand's Object.is
  // check skips re-renders for disciplines the sort did not affect — O(1) instead
  // of O(N) setOrderedPositions calls per hydrateProps.
  const thisDiscipline = usePositionsTable((s) =>
    s.disciplines.find((d) => d.id === discipline.id)
  )

  // getPositionColumns is a stable store reference that reads fresh data via
  // get() on every call, so disciplines is not needed as a dep here.
  const getRealPositions = React.useCallback((): TPosition[] => {
    const columns = getPositionColumns(discipline.id)
    const positions: TPosition[] = []
    columns.forEach((posArray) => positions.push(...posArray))
    return positions.sort(
      (a, b) => (a.seniorityLevel ?? 0) - (b.seniorityLevel ?? 0)
    )
  }, [discipline.id, getPositionColumns])

  const [orderedPositions, setOrderedPositions] =
    React.useState<TPosition[]>(getRealPositions)

  const [activePosition, setActivePosition] = React.useState<TPosition | null>(
    null
  )

  const isSaving = React.useRef(false)

  // Keep in sync after hydrateProps refreshes the store. Scoped to
  // thisDiscipline so only the affected DisciplineSection re-syncs.
  React.useEffect(() => {
    setOrderedPositions(getRealPositions())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thisDiscipline])

  const ids = orderedPositions.map((p) => `pos-${p.id}`)

  const reset = React.useCallback(() => {
    setOrderedPositions(getRealPositions())
  }, [getRealPositions])

  const handleDragStart = React.useCallback(
    (e: DragStartEvent) => {
      const id = e.active.id.toString()
      const position = orderedPositions.find((p) => `pos-${p.id}` === id)
      if (position) setActivePosition(position)
    },
    [orderedPositions]
  )

  // No list re-ordering during drag — the drop target is resolved on DragEnd
  const handleDragOver = () => {}

  const handleDragEnd = async (e: DragEndEvent) => {
    setActivePosition(null)

    // Reject concurrent drags: reset UI to store state and bail
    if (isSaving.current) {
      reset()
      return
    }

    if (!activePosition?.id || !activePosition.seniorityLevel || !e.over?.id) {
      return
    }

    const targetLevel = resolveTargetSeniorityLevel(
      e.over.id.toString(),
      getRealPositions()
    )

    if (targetLevel === null || targetLevel === activePosition.seniorityLevel) {
      return
    }

    const currentPositions = getRealPositions()
    const newPositions = applyBlockMove(
      currentPositions,
      activePosition.id,
      targetLevel
    )

    // Sync customLabel for positions whose label was the auto-generated default
    // (discipline initials + seniority level, e.g. "AT2"). Mirrors the backend
    // sort action which updates custom_label under the same condition.
    // Positions with a genuinely custom label (e.g. "Team Lead") are left alone.
    const initials = (discipline.name || '')
      .split(' ')
      .map((w) => w[0]?.toUpperCase() ?? '')
      .join('')
    const newPositionsWithLabels = newPositions.map((p) => {
      const original = currentPositions.find((op) => op.id === p.id)
      const oldDefaultLabel = `${initials}${
        original?.seniorityLevel ?? p.seniorityLevel
      }`
      return p.customLabel === oldDefaultLabel
        ? { ...p, customLabel: `${initials}${p.seniorityLevel}` }
        : p
    })

    // Optimistic UI: update local order and the store so positionColumns
    // re-renders immediately without waiting for hydrateProps to complete.
    // Read disciplines via getState() — no subscription needed here since this
    // runs inside an event handler, not during render.
    setOrderedPositions(newPositionsWithLabels)
    const originalDisciplines = usePositionsTable.getState().disciplines ?? []
    setDisciplines(
      originalDisciplines.map((d: TDiscipline) =>
        d.id === discipline.id ? { ...d, positions: newPositionsWithLabels } : d
      )
    )

    isSaving.current = true
    try {
      const success = await eventHandlers.onMovePositionToBlock?.(
        discipline.id,
        activePosition.id,
        targetLevel
      )
      if (!success) {
        setDisciplines(originalDisciplines)
        reset()
      }
    } finally {
      isSaving.current = false
    }
  }

  const handleDragCancel = React.useCallback(() => {
    setActivePosition(null)
    reset()
  }, [reset])

  return {
    ids,
    activePosition,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
  }
}
